import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { Component, inject, input, OnInit, signal } from '@angular/core';
import { ControlValueAccessor, FormsModule, NgControl, ValidationErrors } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatError, MatFormField, MatHint, MatPrefix, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { MatInput } from '@angular/material/input';

import { TranslationPipe } from '@rent2end/core/translation';

import { ValidationErrorDirective } from './validation-error.directive';

@Component({
  selector: 'r2e-file-input',
  imports: [
    FormsModule,
    MatButton,
    MatError,
    MatFormField,
    MatHint,
    MatInput,
    MatIcon,
    MatIconButton,
    MatPrefix,
    MatSuffix,
    TranslationPipe,
    ValidationErrorDirective,
  ],
  templateUrl: './file-input.component.html',
  styleUrl: './file-input.component.scss',
})
export class FileInputComponent implements ControlValueAccessor, OnInit {
  private ngControl = inject(NgControl);
  
  accept = input<string>();
  fileNameTemplate = input(null, { transform: this.transformStringIntoTemplate });
  hint = input<string>();
  label = input<string>();
  maxSize = input<number>();
  multiple = input(false, { transform: coerceBooleanProperty });

  disabled = signal(false);
  fileInputs = signal<{ file: File, originalName: string }[]>([]);
  
  get errors(): ValidationErrors | null {
    return this.ngControl.control?.errors ?? null;
  }
  
  get ivalid(): boolean {
    return !!(this.ngControl.touched && this.ngControl.invalid);
  }

  onChange?: (files: File[]) => void;
  onTouched?: () => void;

  constructor() {
    this.ngControl.valueAccessor = this;
  }

  ngOnInit(): void {
    this.ngControl.control?.addValidators(() => this.maxSizeErrors());
    this.ngControl.control?.updateValueAndValidity({ emitEvent: false });
  }

  registerOnChange(fn: (files: File[]) => void): void {
    this.onChange = fn;
  }
  
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  exceedsMaxSize({ size }: File): boolean {
    const maxSize = this.maxSize();
    return maxSize != null && size > maxSize * 1024 * 1024;
  }

  removeFile(index: number): void {
    this.fileInputs.update(input => input.filter((_, i) => i !== index));
    this.notifyChange();
  }

  selectFiles({ target }: Event): void {
    const { files } = target as HTMLInputElement;

    if (files) {
      const fileInputs = this.transformFilesIntoFileInputs(Array.from(files));

      if (this.multiple()) {
        this.fileInputs.update(inputs => [...inputs, ...fileInputs].map((input, index) => ({
          file: new File([input.file], this.fileNameTemplate()?.(index + 1) ?? input.originalName, { type: input.file.type }),
          originalName: input.originalName,
        })));
      } else {
        this.fileInputs.set(fileInputs);
      }

      this.notifyChange();

      (target as HTMLInputElement).value = '';
    }
  }
  
  setDisabledState?(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  updateFileName(index: number, name: string): void {
    this.fileInputs.update((inputs) =>
      inputs.map((input, i) => {
        if (i === index) {
          return {
            file: new File([input.file], name, { type: input.file.type }),
            originalName: input.originalName,
          };
        } else {
          return input;
        }
      }),
    );
    this.notifyChange();
  }

  writeValue(files: File[]): void {
    this.fileInputs.set(this.transformFilesIntoFileInputs(files));
  }

  private maxSizeErrors(): ValidationErrors | null {
    const maxSize = this.maxSize();

    if (maxSize == null) {
      return null;
    }

    const oversized = this.fileInputs().some(({ file }) => this.exceedsMaxSize(file));

    return oversized ? { maxSize: { maxSize } } : null;
  }

  private notifyChange(): void {
    this.onChange?.(this.fileInputs().map(input => input.file));
  }

  private transformFilesIntoFileInputs(files: File[]): { file: File, originalName: string }[] {
    return files.map(file => ({
      file: new File([file], file.name, { type: file.type }),
      originalName: file.name,
    }));
  }

  private transformStringIntoTemplate(string: string): (...args: Array<string | number>) => string {
    return (...args: Array<string | number>) => string.replace(/\$\{(\d+)\}/g, (_, i) => args[i] as string);
  }
}
